// Model3D — THE 3D view. Every surface that shows a model renders this, never its own
// viewer (Alex, 2026-10-15: "standard for every use & future of the 3D").
//
// What every 3D view gets from here, by construction:
//   - Onshape gestures: right-drag rotate, middle-drag (or Ctrl/Shift + right-drag) pan,
//     wheel zoom at the cursor, F fit, Shift+1..7 standard views;
//   - a Perspective toggle: off = orthographic everywhere; the 2D views (Front, Top,
//     Right) go orthographic by themselves and a rotate drag comes back to perspective;
//   - the opening view along the model's longest horizontal axis (viewer3d/engine.js);
//   - the gesture list printed under the view, so nobody has to guess.
//
// The engine (three.js, pinned) is a separate bundle fetched the first time a 3D view is
// actually on screen, so pages without 3D never download it.
//
//   src        — URL of the GLB. The caller owns the route (internal or /api/customer/…),
//                which keeps this component free of any route and invisible to the
//                customer-view gate's rule 1.
//   height     — px
//   maxWidth   — px; the card's aspect cap (OnshapeSection's MAX_ASPECT)
//   background — CSS colour (a theme token)
//   onStatus   — ('loading' | 'loaded' | 'error: …') => void

import { useEffect, useRef, useState } from 'react';
import Text from './Text.jsx';
import { BTN_SECONDARY, BTN_PRIMARY_OUTLINE, BTN_DISABLED } from '../theme-tokens.js';

// The engine bundle's hashed name, written into index.html by build-web.mjs.
function engineUrl() {
  const meta = document.querySelector('meta[name="viewer3d"]');
  return meta ? meta.getAttribute('content') : '/viewer3d.js';
}
let enginePromise = null;
function loadEngine() {
  // A variable, not a literal: esbuild must leave this import() alone so the engine
  // stays its own file instead of being folded into the main bundle.
  if (!enginePromise) {
    const url = engineUrl();
    enginePromise = import(url).catch((e) => { enginePromise = null; throw e; });
  }
  return enginePromise;
}

const VIEWS = [
  ['Iso', 'iso', 'Shift+7'],
  ['Front', 'front', 'Shift+1'],
  ['Top', 'top', 'Shift+5'],
  ['Right', 'right', 'Shift+4'],
];

export const GESTURE_HELP =
  'Right-drag: rotate · Middle-drag or Ctrl+right-drag: pan · Wheel: zoom at cursor · F: fit · Shift+1–7: views';

export default function Model3D({ src, height = 420, maxWidth = null, background = 'var(--th-row-even)', onStatus = null }) {
  const boxRef = useRef(null);
  const viewRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [ready, setReady] = useState(false);
  const [ortho, setOrtho] = useState(false);
  const statusRef = useRef(onStatus);
  statusRef.current = onStatus;

  // Mount the engine only once the box is on screen — "Expand all" can put several
  // models on one page, and each live view holds a WebGL context.
  useEffect(() => {
    const el = boxRef.current;
    if (!el || visible) return;
    if (typeof IntersectionObserver === 'undefined') { setVisible(true); return; }
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) { setVisible(true); io.disconnect(); }
    }, { rootMargin: '200px' });
    io.observe(el);
    return () => io.disconnect();
  }, [visible]);

  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    let view = null;
    statusRef.current?.('loading');
    loadEngine().then(({ createViewer }) => {
      if (cancelled || !boxRef.current) return;
      view = createViewer(boxRef.current, {
        onProjection: (o) => setOrtho(o),
        onStatus: (s) => statusRef.current?.(s),
      });
      viewRef.current = view;
      setReady(true);
    }).catch((e) => statusRef.current?.('error: 3D engine did not load (' + (e?.message || e) + ')'));
    return () => {
      cancelled = true;
      if (view) view.dispose();
      viewRef.current = null;
      setReady(false);
    };
  }, [visible]);

  useEffect(() => {
    if (ready && src && viewRef.current) viewRef.current.load(src);
  }, [ready, src]);

  const act = (fn) => () => { if (viewRef.current) fn(viewRef.current); };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, width: '100%', maxWidth: maxWidth || undefined }}>
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
        {VIEWS.map(([label, name, key]) => (
          <button key={name} type="button" style={{ ...BTN_SECONDARY, ...(ready ? null : BTN_DISABLED) }} disabled={!ready}
            title={`${label} view (${key})`} onClick={act((v) => v.setView(name))}>{label}</button>
        ))}
        <button type="button" style={{ ...BTN_SECONDARY, ...(ready ? null : BTN_DISABLED) }} disabled={!ready} title="Zoom to fit (F)"
          onClick={act((v) => v.fit())}>Fit</button>
        {/* Words carry the state, not the fill alone: "Perspective: on/off". */}
        <button type="button" style={{ ...(ortho ? BTN_SECONDARY : BTN_PRIMARY_OUTLINE), ...(ready ? null : BTN_DISABLED) }} disabled={!ready}
          aria-pressed={!ortho}
          title="Off = orthographic (no perspective) in every view"
          onClick={act((v) => v.toggleOrtho())}>Perspective: {ortho ? 'off' : 'on'}</button>
      </div>
      <div
        ref={boxRef}
        tabIndex={-1}
        onContextMenu={(e) => e.preventDefault()}
        style={{ width: '100%', height, background, borderRadius: 6, overflow: 'hidden', position: 'relative' }}
      />
      <Text kind="hint">{GESTURE_HELP}</Text>
    </div>
  );
}
